import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import {
  BodyEvent,
  BodyObjectUpdate,
  CarCard,
  CardsService,
  CarEpisode,
  CarEvent,
  CarObjectUpdate,
  FaceEvent,
  FaceObjectUpdate,
  HumanCard,
  HumanEpisode,
  ObjectsService
} from '@/api';
import { ItemsActionNames } from '@/definitions/app/item.actions.name';
import { configModule } from '@/store/config';
import { configAclModule } from '@/store/config/acl';
import { loadDataModule } from '@/store/data/LoadDataModule';
import { RouterModule } from '@/store/router';
import NButton from '@/uikit/buttons/NButton.vue';
import { copyTextToClipboard } from '@/uikit/helpers';
import NHint from '@/uikit/hint/NHint.vue';
import NIcon from '@/uikit/icons/NIcon.vue';
import { EventDetails, EventTypes, ThumbnailPositions } from '@/uikit/thumbnail/helpers/enums';
import NThumbnail from '@/uikit/thumbnail/NThumbnail.vue';
import Attributes from '@/components/common/Attributes.vue';
import DataSourceLabels from '@/components/common/DataSourceLabels.vue';
import Confidence from '@/components/common/Confidence.vue';
import { DirectionEnum as AcknowledgeDirectionEnum } from '@/components/common/deprecated/acknowledge-types';
import Acknowledge from '@/components/common/deprecated/Acknowledge.vue';
import DoubleThumbnail from '@/components/common/DoubleThumbnail.vue';
import Features from '@/components/common/Features.vue';
import WatchListsGroup from '@/components/common/WatchListsGroup.vue';
import { AnyEpisode, AnyEvent, EventOpenedItem, EventOpenedItems, EventOrEpisode } from '@/components/events/types';
import { episodeAdapter, eventAdapter, EventView } from './adapter';
import EventItemAttributeLineCross from './EventItemAttributeLineCross.vue';
import { EpisodeType, EventType, EventTypesMap } from '@/store/application/data.assets';
import { actionHandler } from '@/store/data/ActionHandler';
import { DisplayType, DisplayTypes } from '@/store/application/page.definitions';
import MatchedCardHelper from '@/common/MatchedCardHelper';

@Options({
  components: {
    Acknowledge,
    Attributes,
    DataSourceLabels,
    Confidence,
    DoubleThumbnail,
    EventItemAttributeLineCross,
    Features,
    NButton,
    NHint,
    NIcon,
    NThumbnail,
    WatchListsGroup
  }
})
export default class EventItem extends Vue {
  @Prop({ type: Object, required: true })
  item!: EventOrEpisode;

  @Prop({ type: String, default: DisplayTypes.Short })
  displayType!: DisplayType;

  @Prop({ type: String, required: true })
  episodeType!: EpisodeType;

  @Prop({ type: String, default: EventTypesMap.Events })
  type!: EventType;

  @Prop({ type: String, required: true })
  objects!: string;

  @Prop({ type: Boolean, default: false })
  eventSelected!: boolean;

  @Prop({ type: Boolean, default: false })
  cardSelected!: boolean;

  @Prop({ type: Boolean, default: false })
  hideAcknowledge!: boolean;

  @Prop({ type: Boolean, default: true })
  acknowledgeAllowed!: boolean;

  @Prop({ type: String, default: '' })
  openedItem!: EventOpenedItem;

  @Prop({ type: String, required: false })
  defaultAction?: string;

  @Prop({ type: Array })
  iconActions?: [];

  matchedCardHelper = new MatchedCardHelper();

  async mounted() {
    if (this.item.matched_card) {
      const isCarCard = this.objects === 'cars' || (this.type === 'episodes' && this.episodeType === 'cars');
      this.matchedCardHelper.isLoadObject = true;
      this.matchedCardHelper.type = isCarCard ? 'cars' : 'humans';
      this.matchedCardHelper.cardId = this.item.matched_card;
    }
  }

  get hasLineCross() {
    return (this.item as AnyEpisode).lines?.length || (this.item as AnyEvent).line;
  }

  get thumbnailActions() {
    const result = [
      { icon: 'check', name: EventTypes.Select, position: ThumbnailPositions.TopLeft, selected: this.eventSelected },
      { icon: 'info', name: EventDetails.ShowInfo, position: ThumbnailPositions.TopRight },
      { icon: 'eye-open', name: EventDetails.ShowFullScreen, position: ThumbnailPositions.BottomRight }
    ];

    if (configModule.features.vms_enabled && !this.item.video_archive) {
      result.push({ icon: 'play', name: EventDetails.ShowPlayer, position: ThumbnailPositions.BottomLeft });
    }

    return this.iconActions || result;
  }

  get isShort() {
    return this.displayType === 'short';
  }

  get isEpisode() {
    return this.type === 'episodes';
  }

  get areCars() {
    return this.isEpisode ? this.episodeType === 'cars' : this.objects === 'cars';
  }

  get thumbnail() {
    return this.isEpisode ? this.event.thumbnail : (this.item as AnyEvent).thumbnail;
  }

  get matchedObjectThumbnail() {
    return this.matchedCardHelper.object?.thumbnail;
  }

  get isLiveEpisode() {
    return this.isEpisode && this.eventView.open;
  }

  get fullThumbnailsClasses() {
    return {
      'event-item-full__thumbnails': true,
      'event-item-full__thumbnails_cars': this.areCars
    };
  }

  get counterClasses() {
    return {
      'label-s': true,
      'events-count': true,
      'events-count_live': this.isLiveEpisode
    };
  }

  get hasThumbnail() {
    return !!this.thumbnail;
  }

  get licensePlate() {
    return this.eventView?.license_plate_thumbnail;
  }

  get event() {
    const { last_face_event, last_body_event, best_face_event, best_body_event } = this.item as HumanEpisode;
    const { last_car_event, best_car_event } = this.item as CarEpisode;

    const last_event = last_face_event ?? last_body_event ?? last_car_event ?? '';
    const best_event = best_face_event ?? best_body_event ?? best_car_event;

    return (this.item as HumanEpisode | CarEpisode).open ? last_event : best_event;
  }

  get verticalAcknowledgeDirection() {
    return AcknowledgeDirectionEnum.Vertical;
  }

  get eventView(): EventView {
    return this.isEpisode ? episodeAdapter(this.item as HumanEpisode | CarEpisode) : eventAdapter(this.item as FaceEvent | BodyEvent | CarEvent, this.objects);
  }

  get eventItemContainerClasses() {
    return {
      'event-item__container': true,
      'event-item-short__container': this.isShort,
      'event-item-full__container': !this.isShort,
      'event-item-full__event-container': !this.isShort,
      'event-item__container_contained-in-sidebar': this.eventSelected,
      'event-item__container_selected': this.openedItem === EventOpenedItems.Item
    };
  }

  get cardItemContainerClasses() {
    return {
      'event-item__container': true,
      'event-item-short__container': this.isShort,
      'event-item-full__container': !this.isShort,
      'event-item__container_contained-in-sidebar': this.cardSelected,
      'event-item__container_selected': this.openedItem === EventOpenedItems.Card
    };
  }

  get cardCibrButtonStyle() {
    return { color: this.cardSelected ? 'var(--color-on-color-100)' : 'var(--color-primary-200)' };
  }

  get licencePlateNumber() {
    if (this.isEpisode && this.episodeType === 'cars') {
      return (this.item as CarEpisode).car_features?.license_plate_number;
    } else if (this.objects === 'cars') return (this.item as CarEvent).features?.license_plate_number;
    else return '';
  }

  get configAclModule() {
    return configAclModule;
  }

  get attributesColor() {
    return this.eventSelected ? 'var(--color-on-color-300)' : '';
  }

  get extraAttributesSlot() {
    // order matter
    if (this.eventView.cars_features) return 'cars-extra-attributes';
    if (this.eventView.bodies_features) return 'bodies-extra-attributes';
    return 'faces-extra-attributes';
  }

  get attributesItems() {
    return {
      faces: { display: !!this.eventView.faces_features, features: this.eventView.faces_features },
      bodies: { display: !!this.eventView.bodies_features, features: this.eventView.bodies_features },
      cars: { display: !!this.eventView.cars_features, features: this.eventView.cars_features }
    };
  }

  get watchListsColor() {
    return this.cardSelected ? 'var(--color-on-color-100)' : '';
  }

  get date() {
    return (
      this.$filters.formatDate(this.eventView.created_date) +
      ' ' +
      this.$filters.formatTime(this.eventView.created_date) +
      (this.eventView.closed_date ? ' - ' + this.$filters.formatTime(this.eventView.closed_date) : '')
    );
  }

  get objectsType() {
    return (this.isEpisode ? this.eventView?.objectsType : this.objects) ?? this.objects;
  }

  get rootComputedStyle() {
    return window.getComputedStyle(document.documentElement);
  }

  computedCommentHeight() {
    const { card_comment } = this.$refs;
    const comment = card_comment?.offsetTop;
    if (comment) {
      const thumbnailSize = parseFloat(this.rootComputedStyle.getPropertyValue('--thumbnail-large-size'));
      const lineHeightComment = parseFloat(this.rootComputedStyle.getPropertyValue('--line-height-5'));
      return Math.floor((thumbnailSize - comment) / lineHeightComment);
    }
  }

  navigateToCase() {
    RouterModule.navigateToCase(this.item.case);
  }

  showCardFullFrame() {
    this.matchedCardHelper.showFullscreen();
  }

  idClickHandler(text: string | number) {
    copyTextToClipboard(String(text));
  }

  @Watch('item.matched_card')
  async changeCardHandler(v: number | null) {
    const isCarCard = this.objects === 'cars' || (this.type === 'episodes' && this.episodeType === 'cars');
    this.matchedCardHelper.type = isCarCard ? 'cars' : 'humans';
    this.matchedCardHelper.cardId = v;
  }

  @Watch('item.matched_object')
  async changeMatchedObjectHandler(v: FaceObjectUpdate | BodyObjectUpdate | CarObjectUpdate | null) {
    if (!v) {
      this.matchedCardHelper.object = null;
    } else {
      const { matched_object: id } = this.eventView as any;
      this.matchedCardHelper.forceObjectId = id;
      this.matchedCardHelper.loadObject();
    }
  }
}
